import React from 'react'
// import './journey-details.scss'
import { AndroidFilled, AppleFilled, ChromeFilled, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import { AppState } from '../../../stores/app'
import { AppService, JourneyService, DomainService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { TabbedView } from '../../../components/tabbed-view/tabbed-view'
import { JourneySummaryTab } from './journey-summary-tab'
import { Journey, getJourneyEndBehavior } from '../types/journey-api-response'
import * as moment from 'moment-timezone'
import { BASE_TIME_FORMAT_WITHOUT_TZ, SHORT_DATE_FORMAT, TZ_PLATFORM_DEFAULT } from '../../../constants'
import { resolveJourneyDisplayTimeZone } from '../helpers/journey-time-zone'
import { DomainDto } from '../../../dtos/domain'
import { Moment } from 'moment'
import { preventBubbling, titleCase } from '../../../_utils/utils'
import autobind from 'autobind-decorator'
import { observe } from 'mobx'
import { ExitBehavior, ExitStatus, JourneyType, StepType } from '@pushly/models/lib/enums/journeys'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import { getTriggerDisplayLabel } from '../trigger-config'
import { PageHeader } from '@pushly/aqe/lib/components'
import {
    DetailsSummaryWell,
    DetailsSummarySection,
    DetailsSummarySubSections,
    DetailsSummaryField,
} from '@pushly/aqe/lib/components/details-summary-well'
import { JourneyWorkflowTab } from '../journey-revisions/journey-workflow-tab'
import { JourneyNotificationsComparisonsTab } from '../journey-notifications/journey-notifications-comparisons-tab'
import { NoTranslate } from '../../../components/no-translate/no-translate'
import { onResponseError403 } from '../../../_utils/on-response-error-403'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { StatusType } from '../../../enums/status-type'
import { asCaslSubject, CurrentUserCan } from '../../../stores/app-ability'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { Button } from '@pushly/aqe/lib/components'
import { Flex, Tag } from 'antd'
import { JOURNEY_FIELD_DESCRIPTIONS } from '../field-descriptions'
import { JourneyStatusBadge } from '../../../components/badges/journey-status-badge'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'

interface IHeaderProps {
    domain: DomainDto
    journey?: Journey
}

interface IHeaderState {
    segments: Array<{ id: number; name: string; is_default?: boolean }>
    excludedSegments: Array<{ id: number; name: string }>
    detailsCollapsed: boolean
}

class JourneyDetailsHeader extends React.Component<IHeaderProps, IHeaderState> {
    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService

    public constructor(props: IHeaderProps) {
        super(props)

        this.appService = Container.get(AppService)
        this.appState = Container.get(AppState)
        this.domainService = Container.get(DomainService)

        this.state = {
            segments: [],
            excludedSegments: [],
            detailsCollapsed: true,
        }
    }

    public componentDidMount(): void {
        this.loadSegments()
    }

    public componentDidUpdate(prevProps: IHeaderProps): void {
        if (prevProps.journey !== this.props.journey) {
            this.loadSegments()
        }
    }

    public render(): React.ReactNode {
        const quietHoursValue = this.journey ? this.buildQuietHoursValue() : null
        const frequencyCapValue = this.journey ? this.buildFrequencyCapValue() : null
        const abandonmentWindowValue = this.journey ? this.buildAbandonmentWindowValue() : null
        const audienceValue = this.buildAudienceValue()
        const excludedAudienceValue = this.buildExcludedAudienceValue()

        return (
            <div className="details-header">
                <div className="journey-title">
                    {!!this.journey && (
                        <PageHeader
                            title={this.journey.name}
                            append={
                                <span>
                                    #{this.journey.id} | Created by{' '}
                                    <NoTranslate>{this.journey.created_by_user_name}</NoTranslate>
                                </span>
                            }
                            action={
                                this.journey.status_id !== StatusType.COMPLETED.id && (
                                    <CurrentUserCan
                                        do={AbilityAction.UPDATE}
                                        on={asCaslSubject(SubjectEntity.CAMPAIGN, this.journey)}
                                    >
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                this.appService.routeWithinDomain(
                                                    `/journeys/${this.journey.id}/builder`,
                                                )
                                            }}
                                            altHref={this.appService.routeWithinDomain(
                                                `/journeys/${this.journey.id}/builder`,
                                                true,
                                            )}
                                        >
                                            <span>Edit Journey</span>
                                        </Button>
                                    </CurrentUserCan>
                                )
                            }
                            onTitleSet={this.appService.customizeTabTitle}
                        />
                    )}
                </div>

                <DetailsSummaryWell loading={!this.journey} collapsible={true} title="Journey Details">
                    <DetailsSummarySection title="General">
                        <DetailsSummaryField label="ID" value={this.journey?.id} copyable />
                        <DetailsSummaryField
                            label="Status"
                            value={this.journey ? this.buildStatusValue() : undefined}
                        />
                        <DetailsSummaryField label="Channels" value={this.buildChannelValues()} />
                        <DetailsSummaryField
                            label="Type"
                            value={this.journey ? titleCase(this.journey.type) : undefined}
                        />
                    </DetailsSummarySection>

                    <DetailsSummarySubSections>
                        <DetailsSummarySection title="Delivery">
                            <DetailsSummaryField
                                label="Date Range"
                                value={this.journey ? this.buildDateRangeValue() : undefined}
                                info={{ title: JOURNEY_FIELD_DESCRIPTIONS.dateRange, inline: true }}
                            />
                            <DetailsSummaryField
                                label="Entry Trigger"
                                value={this.journey ? this.buildEntryTriggerValue() : undefined}
                                info={{ title: JOURNEY_FIELD_DESCRIPTIONS.entryTrigger, inline: true }}
                            />
                            {quietHoursValue != null && (
                                <DetailsSummaryField
                                    label="Quiet Hours"
                                    value={quietHoursValue}
                                    info={{ title: JOURNEY_FIELD_DESCRIPTIONS.quietHours, inline: true }}
                                />
                            )}
                            {frequencyCapValue != null && (
                                <DetailsSummaryField
                                    label="Frequency Cap"
                                    value={frequencyCapValue}
                                    info={{ title: JOURNEY_FIELD_DESCRIPTIONS.frequencyCap, inline: true }}
                                />
                            )}
                        </DetailsSummarySection>

                        {/* TODO: For subscription triggers, expose custom audience criteria (pquery) here */}
                        {(audienceValue != null || excludedAudienceValue != null) && (
                            <DetailsSummarySection title="Targeting">
                                {audienceValue != null && (
                                    <DetailsSummaryField label="Audience(s)" value={audienceValue} />
                                )}
                                {excludedAudienceValue != null && (
                                    <DetailsSummaryField label="Excluded Audience(s)" value={excludedAudienceValue} />
                                )}
                            </DetailsSummarySection>
                        )}
                    </DetailsSummarySubSections>

                    <DetailsSummarySection title="Behavior">
                        <DetailsSummaryField
                            label="End Behavior"
                            value={this.journey ? this.buildEndBehaviorValue() : undefined}
                            info={{ title: JOURNEY_FIELD_DESCRIPTIONS.endBehavior, inline: true }}
                        />
                        {abandonmentWindowValue != null && (
                            <DetailsSummaryField
                                label="Abandonment Window"
                                value={abandonmentWindowValue}
                                info={{ title: JOURNEY_FIELD_DESCRIPTIONS.abandonmentWindow, inline: true }}
                            />
                        )}
                    </DetailsSummarySection>
                </DetailsSummaryWell>
            </div>
        )
    }

    protected get journey(): Journey {
        return this.props.journey!
    }

    protected async loadSegments(): Promise<void> {
        const journey = this.props.journey
        if (!journey) return

        const segmentIds = journey.configuration?.audience?.segment_ids ?? []
        const excludedSegmentIds = journey.configuration?.audience?.excluded_segment_ids ?? []
        if (segmentIds.length === 0 && excludedSegmentIds.length === 0) return

        const { ok, data } = await this.domainService.fetchSegmentsByDomainId(this.props.domain.id, {
            showLoadingScreen: false,
        })

        if (ok && data) {
            const allIds = new Set([...segmentIds, ...excludedSegmentIds])
            const relevantSegments = (data as Array<{ id: number; name: string; is_default?: boolean }>).filter((s) =>
                allIds.has(s.id),
            )

            this.setState({
                segments: relevantSegments.filter((s) => segmentIds.includes(s.id)),
                excludedSegments: relevantSegments.filter((s) => excludedSegmentIds.includes(s.id)),
            })
        }
    }

    protected buildAudienceValue(): React.ReactNode {
        const segments = this.state.segments
        if (!segments || segments.length === 0) return undefined

        const isAllSubscribers = segments.find((s) => s.is_default)
        if (isAllSubscribers) {
            return <Tag>All Subscribers</Tag>
        }

        return (
            <>
                {segments
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((segment, idx) => (
                        <a
                            key={`seg-${idx}`}
                            onClick={(ev) => this.jumpToSegment(segment, ev)}
                            href={this.buildSegmentUrl(segment)}
                        >
                            <Tag>{segment.name}</Tag>
                        </a>
                    ))}
            </>
        )
    }

    protected buildExcludedAudienceValue(): React.ReactNode {
        const segments = this.state.excludedSegments
        if (!segments || segments.length === 0) return undefined

        return (
            <>
                {segments
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((segment, idx) => (
                        <a
                            key={`xseg-${idx}`}
                            onClick={(ev) => this.jumpToSegment(segment, ev)}
                            href={this.buildSegmentUrl(segment)}
                        >
                            <Tag>{segment.name}</Tag>
                        </a>
                    ))}
            </>
        )
    }

    protected buildDateRangeValue(): React.ReactNode {
        const journey = this.journey
        if (!journey) {
            return ''
        }

        const isRecurring = journey.type === JourneyType.RECURRING
        const domainTz = this.appState.currentDomain?.timezone ?? TZ_PLATFORM_DEFAULT
        const { tz } = resolveJourneyDisplayTimeZone(journey.date_time_zone, this.appState.currentDomain)

        const dateStart: Moment = moment.tz(journey.date_start, tz)
        const dateStartStr: string = dateStart.format(!isRecurring ? BASE_TIME_FORMAT_WITHOUT_TZ : SHORT_DATE_FORMAT)

        let dateEnd: Moment | null = null
        let dateEndStr: string | null = null
        if (journey.date_end) {
            dateEnd = moment.tz(journey.date_end, tz)
        } else if (journey.date_completed_utc) {
            dateEnd = moment.tz(journey.date_completed_utc, 'UTC').tz(domainTz)
        }
        if (dateEnd) {
            const useFullFmt = !isRecurring || journey.manually_completed
            dateEndStr = dateEnd.format(useFullFmt ? BASE_TIME_FORMAT_WITHOUT_TZ : SHORT_DATE_FORMAT)
        }

        return (
            <span>
                {dateStartStr} through {!dateEndStr ? 'Ongoing' : dateEndStr}
                {journey.manually_completed && <span className="lowlight"> (Manually Completed)</span>}
            </span>
        )
    }

    protected buildEntryTriggerValue(): React.ReactNode {
        const journey = this.journey
        if (!journey) {
            return ''
        }

        const computedType = getTriggerDisplayLabel(
            journey.configuration.computed_trigger_type,
            titleCase(journey.type),
        )

        let triggerSegmentsView: React.ReactNode = ''

        if (Array.isArray(journey.revision?.steps)) {
            const triggerStep = extractJourneyTrigger(journey)
            if (triggerStep?.configuration.params?.criteria) {
                triggerSegmentsView = (
                    <span>
                        {' '}
                        and <b>matches criteria</b>
                    </span>
                )
            }
        }

        return (
            <span>
                {computedType}
                {triggerSegmentsView}
            </span>
        )
    }

    protected buildChannelValues(): React.ReactNode {
        const channels = this.journey?.channels
        if (!channels) return undefined

        const items: React.ReactNode[] = []
        if (channels.includes(DeliveryChannel.WEB))
            items.push(
                <span key="web">
                    <ChromeFilled /> Web
                </span>,
            )
        if (channels.includes(DeliveryChannel.NATIVE_IOS))
            items.push(
                <span key="ios">
                    <AppleFilled /> Native Apple
                </span>,
            )
        if (channels.includes(DeliveryChannel.NATIVE_ANDROID))
            items.push(
                <span key="android">
                    <AndroidFilled /> Native Android
                </span>,
            )

        if (items.length === 0) return undefined

        return items.reduce<React.ReactNode[]>((acc, item, i) => {
            if (i > 0) acc.push(', ')
            acc.push(item)
            return acc
        }, [])
    }

    protected buildStatusValue(): React.ReactNode {
        return <JourneyStatusBadge status={this.journey.status} expanded={true} />
    }

    protected buildEndBehaviorValue(): string {
        const eb = getJourneyEndBehavior(this.journey)
        if (eb.behavior === ExitBehavior.BLEED) return 'Bleed'
        if (eb.behavior === ExitBehavior.STOP) {
            return `Stop: ${titleCase(eb.status || ExitStatus.COMPLETED)}`
        }
        return titleCase(eb.behavior)
    }

    protected buildQuietHoursValue(): string | null {
        const quietHours = this.journey.configuration?.quiet_hours
        if (!quietHours) return null
        const to12Hour = (time: string): string => {
            const [hourStr, minute] = time.split(':')
            const hour = parseInt(hourStr, 10)
            const period = hour >= 12 ? 'PM' : 'AM'
            const hour12 = hour % 12 || 12
            return `${hour12}:${minute} ${period}`
        }
        return `${to12Hour(quietHours.start)} – ${to12Hour(quietHours.end)} STZ`
    }

    protected buildFrequencyCapValue(): string | null {
        const fcap = this.journey.configuration?.subscriber_fcap
        if (!fcap) return null

        if (fcap.type === 'once') {
            return 'Once per subscriber'
        }

        if (fcap.criteria) {
            const { frequency, window } = fcap.criteria
            // `once_per_criteria` means a subscriber may enter once every <frequency> <window>.
            // Window is stored as plural ("days", "months"); use the singular form when frequency is 1.
            // e.g., "1 entry every day", "1 entry every 7 days", "1 entry every 3 months"
            const windowLabel = frequency === 1 ? window.replace(/s$/, '') : `${frequency} ${window}`
            return `1 entry every ${windowLabel}`
        }

        return 'Once per criteria match'
    }

    protected buildAbandonmentWindowValue(): string | null {
        const seconds = this.journey.configuration?.abandonment_seconds
        if (seconds == null) return null

        const metric = this.journey.configuration?.abandonment_metric
        if (metric) {
            const amount = metric === 'hours' ? seconds / 3600 : metric === 'minutes' ? seconds / 60 : seconds
            return `${amount} ${metric}`
        }

        if (seconds >= 3600) {
            const hours = seconds / 3600
            return `${hours} hour${hours !== 1 ? 's' : ''}`
        }
        if (seconds >= 60) {
            const minutes = seconds / 60
            return `${minutes} minute${minutes !== 1 ? 's' : ''}`
        }
        return `${seconds} second${seconds !== 1 ? 's' : ''}`
    }

    protected buildSegmentUrl(segment: { id: number }): string {
        return this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)
    }

    @autobind
    protected jumpToSegment(segment: { id: number }, ev?: React.MouseEvent): void {
        if (!ev?.metaKey) {
            preventBubbling(ev)
            this.appService.route(this.buildSegmentUrl(segment))
        }
    }
}

interface IJourneyDetailsProps {}

interface IJourneyDetailsState {
    loading: boolean
    domain: DomainDto
    journey?: Journey
}

export class JourneyDetails extends TabbedView<IJourneyDetailsProps, IJourneyDetailsState> {
    protected readonly appState: AppState
    protected readonly domainService: DomainService
    protected readonly journeyService: JourneyService

    protected animated: boolean = false

    private disposeObservers: Array<() => void> = []

    public constructor(props: IJourneyDetailsProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.domainService = Container.get(DomainService)
        this.journeyService = Container.get(JourneyService)

        this.pathStyle = 'hash'

        this.state = {
            loading: true,
            domain: this.appState.currentDomain!,
            tabs: [],
        }
    }

    public async componentDidMount() {
        this.disposeObservers = [observe(this.appState, 'currentDomainJsonData', () => this.setDomainState())]

        await this.setDomainState()

        const remoteLoaders: Array<Promise<any>> = []
        remoteLoaders.push(this.loadRequestedJourney())
        await Promise.all(remoteLoaders)

        return this.loadTabsState()
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.disposeObservers.forEach((fn) => fn())
    }

    public render(): React.ReactNode {
        return (
            <Flex vertical gap={VERTICAL_WELL_GAP}>
                {this.state.journey && <JourneyDetailsHeader domain={this.state.domain} journey={this.state.journey} />}

                {!this.state.loading && this.renderTabs()}
            </Flex>
        )
    }

    protected async setDomainState(): Promise<void> {
        if (!!this.globalDomain) {
            if (!this.localDomain) {
                await this.setState({ domain: this.globalDomain })
            } else if (this.globalDomain.id !== this.localDomain.id) {
                this.goBackToJourneysList()
            }
        }
    }

    protected goBackToJourneysList() {
        this.appService.routeWithinDomain('/journeys')
    }

    protected getJourneyIdFromUrl(): number {
        return this.injectedProps.match.params.journeyId
    }

    protected async loadRequestedJourney(): Promise<void> {
        const journeyId = this.getJourneyIdFromUrl()

        const res = await this.journeyService.fetchById(journeyId, {
            showLoadingScreen: false,
            cancellationKey: 'cd.loadRequestedJourney',
            errorHandler: onResponseError403(() => {
                this.goBackToJourneysList()
            }),
        })

        if (res.ok) {
            return this.setState({
                loading: false,
                journey: !res.data ? undefined : (res.data as Journey),
            })
        }
    }

    protected async loadTabsState(): Promise<void> {
        const tabs = [
            {
                component: JourneySummaryTab,
                props: {
                    journey: this.state.journey,
                    domain: this.appState.currentDomain,
                },
            },
            {
                component: JourneyWorkflowTab,
                props: {
                    journey: this.state.journey,
                    activeTab: this.state.activeTab,
                },
            },
            {
                component: JourneyNotificationsComparisonsTab,
                props: {
                    journey: this.state.journey,
                    level: 'domain',
                    domainId: this.state.domain.id,
                },
            },
        ]

        const activeTab = this.determineActiveTab(tabs)

        return this.setState({
            tabs,
            activeTab,
        })
    }

    protected determineActiveTab(tabs: Array<any>): any {
        const hash = location.hash.replace('#', '')
        if (hash) {
            const matched = tabs.find((tab) => {
                const name = this.isComponentTab(tab) ? tab.component.tabName : tab.name
                return name === hash
            })
            if (matched) return matched
        }

        // Fallback: check if the last URL segment matches a tab name
        // (handles legacy /journeys/:id/summary links)
        const lastUrlPart = location.pathname.split('/').pop()
        const legacyMatch = tabs.find((tab) => {
            const name = this.isComponentTab(tab) ? tab.component.tabName : tab.name
            return name === lastUrlPart
        })
        if (legacyMatch) return legacyMatch

        return tabs[0]
    }

    protected handleTabChange = async (key: string): Promise<void> => {
        const tabs = this.getTabs()
        const nextActiveTab = tabs.find((tab: any) => {
            const tabName = this.getTabName(tab) ?? 'null'
            return tabName === key
        })

        if (this.props.canTabNavigate) {
            const canNavigate = this.props.canTabNavigate()
            if (!canNavigate) return
        }

        const tabUrl = `${location.origin}${location.pathname}#${key}${location.search}`
        history.replaceState({}, document.title, tabUrl)

        await this.setState({ activeTab: nextActiveTab })

        tabs.forEach((tab: any) => {
            if (this.isComponentTab(tab) && this.tabRefs[tab.component.tabName]) {
                const tabRef = this.tabRefs[tab.component.tabName]
                tabRef.tabDidChange?.(key, nextActiveTab)
            }
        })

        this.props.onTabChange?.(nextActiveTab)
    }
}
